/**
 * LoginGate — app-wide entry gate.
 *
 * On the deployed app, nothing renders until the user signs in with a Google
 * account on the allowed domain (@klugeinteractive.com, enforced server-side in
 * /api/auth/google). A username/password admin login is kept as a fallback.
 *
 * On localhost (dev) the gate is bypassed so development isn't slowed down.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { getGoogleConfig, googleSignIn, loginMapper, requestOtp, verifyOtp } from '../../api/authClient';
import ParticleWaveBackground from '../dashboard/ParticleWaveBackground';
import { color, font } from '../ui/theme';

declare global {
  interface Window { google?: any }
}

const IS_LOCALHOST =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)$/.test(window.location.hostname);

// Escape hatch: append ?gate=1 to the URL to force the login screen on localhost
// (handy for testing the Google flow without removing the dev bypass).
const FORCE_GATE =
  typeof window !== 'undefined' && /[?&]gate=1(\b|$)/.test(window.location.search);

export default function LoginGate({ children }: { children: React.ReactNode }) {
  const mapper = useAuthStore(s => s.mapper);
  if (mapper) return <>{children}</>;
  // Dev convenience: never gate localhost (unless ?gate=1). In prod, require a session.
  if (IS_LOCALHOST && !FORCE_GATE) return <>{children}</>;
  return <LoginScreen />;
}

function LoginScreen() {
  const setSession = useAuthStore(s => s.setSession);
  const [cfg, setCfg] = useState<{ enabled: boolean; clientId: string; domain: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminUser, setAdminUser] = useState('admin');
  const [adminPass, setAdminPass] = useState('');
  // Email one-time-code flow. mode: 'google' shows the Google button; 'email'
  // shows the two-step OTP form (step 'request' → 'verify').
  const [mode, setMode] = useState<'google' | 'email'>('google');
  const [otpStep, setOtpStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [otpMsg, setOtpMsg] = useState('');
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => { getGoogleConfig().then(setCfg); }, []);

  // Initialise Google Identity Services + render the official button.
  useEffect(() => {
    if (!cfg?.enabled || !cfg.clientId) return;
    let tries = 0;
    let timer = 0;
    const init = () => {
      const g = window.google;
      if (!g?.accounts?.id) {
        if (tries++ < 60) timer = window.setTimeout(init, 100); // wait for the GIS script
        return;
      }
      g.accounts.id.initialize({
        client_id: cfg.clientId,
        auto_select: false,          // never silently reuse a session → always let the user pick
        callback: async (resp: { credential: string }) => {
          setBusy(true); setErr('');
          try {
            const { mapper, token } = await googleSignIn(resp.credential);
            setSession(mapper, token);
          } catch (e: any) {
            setErr(e?.message || 'Google sign-in failed.');
            setBusy(false);
          }
        },
      });
      // Forget any remembered account so the chooser is shown.
      try { g.accounts.id.disableAutoSelect(); } catch { /* ignore */ }
      if (btnRef.current) {
        g.accounts.id.renderButton(btnRef.current, {
          theme: 'filled_black', size: 'large', text: 'signin_with',
          shape: 'pill', width: 280, logo_alignment: 'center',
        });
      }
    };
    init();
    return () => { if (timer) clearTimeout(timer); };
  }, [cfg, setSession]);

  const submitAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { mapper, token } = await loginMapper(adminUser.trim(), adminPass);
      setSession(mapper, token);
    } catch (e: any) {
      setErr(e?.message || 'Login failed.');
      setBusy(false);
    }
  };

  const submitOtpRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(''); setOtpMsg('');
    try {
      const { message } = await requestOtp(email.trim());
      // Always advance regardless of whether the email is authorized (the
      // backend returns a generic message either way).
      setOtpMsg(message || 'If that email is authorized, a code has been sent.');
      setOtpStep('verify');
    } catch (e: any) {
      setErr(e?.message || 'Could not send code.');
    } finally {
      setBusy(false);
    }
  };

  const submitOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { mapper, token } = await verifyOtp(email.trim(), code.trim());
      setSession(mapper, token);
    } catch (e: any) {
      setErr(e?.message || 'Invalid or expired code.');
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <ParticleWaveBackground />
      <div style={cardStyle}>
        <div style={brandStyle}>SYNAPSE</div>
        <div style={subStyle}>Mapping Suite</div>

        <div style={{ height: 18 }} />

        {cfg && !cfg.enabled && mode === 'google' && (
          <>
            <div style={noticeStyle}>
              Google sign-in isn't configured on the server (set <code>GOOGLE_CLIENT_ID</code>).
              Sign in with an email code or the admin login below.
            </div>
            <button
              style={adminSubmitStyle}
              onClick={() => { setErr(''); setMode('email'); }}
            >
              Sign in with email code
            </button>
          </>
        )}

        {cfg?.enabled && mode === 'google' && (
          <>
            <div ref={btnRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
            <button
              style={adminToggleStyle}
              onClick={() => {
                try { window.google?.accounts.id.disableAutoSelect(); } catch { /* ignore */ }
                try { window.google?.accounts.id.prompt(); } catch { /* ignore */ }
              }}
            >
              Use a different Google account
            </button>
            <div style={domainHintStyle}>
              Only <strong>@{cfg.domain}</strong> accounts can enter.
            </div>
            <button
              style={adminToggleStyle}
              onClick={() => { setErr(''); setMode('email'); }}
            >
              Sign in with email code
            </button>
          </>
        )}

        {mode === 'email' && (
          <>
            {otpStep === 'request' ? (
              <form onSubmit={submitOtpRequest} style={adminFormStyle}>
                <input
                  style={inputStyle} placeholder="you@example.com" type="email" value={email}
                  onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus
                />
                <button type="submit" style={adminSubmitStyle} disabled={busy || !email.trim()}>
                  {busy ? '…' : 'Send code'}
                </button>
              </form>
            ) : (
              <form onSubmit={submitOtpVerify} style={adminFormStyle}>
                {otpMsg && <div style={noticeStyle}>{otpMsg}</div>}
                <input
                  style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center' }}
                  placeholder="6-digit code" inputMode="numeric" maxLength={6} value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))} autoComplete="one-time-code" autoFocus
                />
                <button type="submit" style={adminSubmitStyle} disabled={busy || code.trim().length < 6}>
                  {busy ? '…' : 'Verify'}
                </button>
                <button
                  type="button" style={adminToggleStyle} disabled={busy}
                  onClick={() => { setErr(''); setCode(''); setOtpStep('request'); }}
                >
                  Resend / change email
                </button>
              </form>
            )}
            <button
              style={adminToggleStyle}
              onClick={() => { setErr(''); setMode('google'); setOtpStep('request'); setCode(''); }}
            >
              {cfg?.enabled ? 'Back to Google sign-in' : 'Back'}
            </button>
          </>
        )}

        {err && <div style={errStyle}>{err}</div>}

        <button style={adminToggleStyle} onClick={() => setShowAdmin(v => !v)}>
          {showAdmin ? 'Hide admin login' : 'Sign in as admin instead'}
        </button>

        {showAdmin && (
          <form onSubmit={submitAdmin} style={adminFormStyle}>
            <input
              style={inputStyle} placeholder="username" value={adminUser}
              onChange={e => setAdminUser(e.target.value)} autoComplete="username"
            />
            <input
              style={inputStyle} placeholder="password" type="password" value={adminPass}
              onChange={e => setAdminPass(e.target.value)} autoComplete="current-password"
            />
            <button type="submit" style={adminSubmitStyle} disabled={busy}>
              {busy ? '…' : 'Sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 100000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'radial-gradient(1200px 600px at 50% 0%, #1a0f3a 0%, #0a0518 60%, #060311 100%)',
  fontFamily: font.sans,
};

const cardStyle: React.CSSProperties = {
  position: 'relative', zIndex: 1,   // above the particle-wave backdrop
  width: 360, maxWidth: '90vw',
  background: 'rgba(15, 10, 35, 0.85)',
  border: '1px solid rgba(124, 58, 237, 0.35)',
  borderRadius: 16,
  padding: '32px 28px',
  boxShadow: '0 20px 80px rgba(0,0,0,0.6)',
  textAlign: 'center',
};

const brandStyle: React.CSSProperties = {
  fontSize: 26, fontWeight: 800, letterSpacing: 3,
  background: 'linear-gradient(90deg, #d946ef, #7c3aed)',
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
};

const subStyle: React.CSSProperties = {
  fontSize: 12, letterSpacing: 2, color: color.accentSoft, textTransform: 'uppercase', marginTop: 2,
};

const domainHintStyle: React.CSSProperties = {
  fontSize: 11, color: color.inkFaint, marginTop: 12,
};

const noticeStyle: React.CSSProperties = {
  fontSize: 11, color: color.inkFaint, lineHeight: 1.5,
  background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)',
  borderRadius: 8, padding: '8px 10px', marginBottom: 12,
};

const errStyle: React.CSSProperties = {
  fontSize: 11, color: '#fca5a5', marginTop: 12,
  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: 8, padding: '8px 10px',
};

const adminToggleStyle: React.CSSProperties = {
  marginTop: 18, background: 'none', border: 'none',
  color: color.inkFaint, fontSize: 10, cursor: 'pointer',
  textDecoration: 'underline', fontFamily: font.sans,
};

const adminFormStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10,
};

const inputStyle: React.CSSProperties = {
  background: 'rgba(10,5,28,0.8)', border: '1px solid rgba(124,58,237,0.3)',
  borderRadius: 8, padding: '8px 10px', color: color.ink, fontSize: 12, fontFamily: font.sans,
};

const adminSubmitStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #7c3aed, #d946ef)', border: 'none',
  borderRadius: 8, padding: '9px 10px', color: '#fff', fontSize: 12, fontWeight: 700,
  cursor: 'pointer', fontFamily: font.sans,
};
